import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Plus, ShoppingCart, Edit, Trash2, X, Search, Check, ChevronsUpDown,
  Eye, PackageCheck, Ban, Package, FileText
} from 'lucide-react';
import { toast } from 'sonner';

import { useAuth, API } from '../../contexts/AuthContext';
import { getErrorMessage } from '../../utils/errorHandler';
import { usePermissions } from '../../hooks/usePermissions';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '../ui/command';
import { formatAmount } from '../../utils/format';

const emptyForm = { supplier: '', notes: '', items: [] };

const statusMeta = {
  pending: { label: 'Pendiente', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  received: { label: 'Recibida', cls: 'bg-green-100 text-green-700 border-green-200' },
  cancelled: { label: 'Cancelada', cls: 'bg-gray-100 text-gray-600 border-gray-200' }
};

const PurchasesTab = () => {
  const { getAuthHeaders } = useAuth();
  const { hasPermission } = usePermissions();

  const [purchases, setPurchases] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [viewOrder, setViewOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [formData, setFormData] = useState(emptyForm);
  const [receiveNow, setReceiveNow] = useState(false);
  const [productPopoverOpen, setProductPopoverOpen] = useState(false);
  const [newProductOpen, setNewProductOpen] = useState(false);
  const [newProductLoading, setNewProductLoading] = useState(false);
  const [newProductForm, setNewProductForm] = useState({ name: '', category_id: '', price: '' });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [purchasesRes, productsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/purchases`, { headers: getAuthHeaders() }),
        axios.get(`${API}/products`, { headers: getAuthHeaders() }),
        axios.get(`${API}/categories`, { headers: getAuthHeaders() })
      ]);
      setPurchases(Array.isArray(purchasesRes.data) ? purchasesRes.data : []);
      setProducts(Array.isArray(productsRes.data) ? productsRes.data : []);
      setCategories(Array.isArray(categoriesRes.data) ? categoriesRes.data : []);
    } catch {
      toast.error('Error al cargar compras');
    }
  };

  const refreshProducts = async () => {
    try {
      const res = await axios.get(`${API}/products`, { headers: getAuthHeaders() });
      setProducts(Array.isArray(res.data) ? res.data : []);
    } catch {
      /* silencio: el alta ya reportó su propio error */
    }
  };

  // ======================= Derivados =======================
  const filteredPurchases = purchases.filter((p) => {
    const q = search.trim().toLowerCase();
    const matchSearch = !q ||
      (p.supplier || '').toLowerCase().includes(q) ||
      String(p.purchase_number || '').includes(q);
    const matchStatus = statusFilter === 'all' || p.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const totalCount = purchases.length;
  const pendingCount = purchases.filter((p) => p.status === 'pending').length;
  const receivedCount = purchases.filter((p) => p.status === 'received').length;
  const receivedValue = purchases
    .filter((p) => p.status === 'received')
    .reduce((s, p) => s + (Number(p.total) || 0), 0);

  const liveItems = formData.items.map((i) => ({
    ...i,
    subtotal: (Number(i.quantity) || 0) * (Number(i.unit_cost) || 0)
  }));
  const liveTotal = liveItems.reduce((s, i) => s + i.subtotal, 0);

  // Opciones del combobox: productos sin variantes -> 1 opción; con variantes -> 1 opción por variante.
  const purchaseOptions = products.flatMap((p) => {
    const variants = Array.isArray(p.variants) ? p.variants.filter((v) => v && v.id) : [];
    if (variants.length === 0) {
      return [{
        key: p.id,
        product_id: p.id,
        variant_id: null,
        variant_name: null,
        label: p.name,
        unit_cost: Number(p.cost) || 0
      }];
    }
    return variants.map((v) => ({
      key: `${p.id}::${v.id}`,
      product_id: p.id,
      variant_id: v.id,
      variant_name: v.name,
      label: `${p.name} ${v.name}`,
      unit_cost: Number(v.cost) || Number(p.cost) || 0
    }));
  });

  const isOptionSelected = (opt) => formData.items.some(
    (i) => i.product_id === opt.product_id && (i.variant_id || null) === (opt.variant_id || null)
  );

  // ======================= Diálogo crear/editar =======================
  const handleOpenCreate = () => {
    setEditingOrder(null);
    setFormData(emptyForm);
    setReceiveNow(false);
    setIsDialogOpen(true);
  };

  const handleEdit = (order) => {
    setEditingOrder(order);
    setFormData({
      supplier: order.supplier || '',
      notes: order.notes || '',
      items: (order.items || []).map((i) => ({
        product_id: i.product_id,
        variant_id: i.variant_id || null,
        variant_name: i.variant_name || null,
        product_name: i.product_name,
        quantity: Number(i.quantity) || 1,
        unit_cost: Number(i.unit_cost) || 0
      }))
    });
    setReceiveNow(false);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setTimeout(() => {
      setEditingOrder(null);
      setFormData(emptyForm);
      setReceiveNow(false);
    }, 100);
  };

  const addProductLine = (option) => {
    setProductPopoverOpen(false);
    setFormData((prev) => {
      const dup = prev.items.some(
        (i) => i.product_id === option.product_id && (i.variant_id || null) === (option.variant_id || null)
      );
      if (dup) {
        toast.warning('El artículo ya está en la orden');
        return prev;
      }
      return {
        ...prev,
        items: [
          ...prev.items,
          {
            product_id: option.product_id,
            variant_id: option.variant_id || null,
            variant_name: option.variant_name || null,
            product_name: option.label,
            quantity: 1,
            unit_cost: option.unit_cost || 0
          }
        ]
      };
    });
  };

  const updateItem = (index, field, value) => {
    setFormData((prev) => {
      const items = prev.items.map((it, idx) => (idx === index ? { ...it, [field]: value } : it));
      return { ...prev, items };
    });
  };

  const removeItem = (index) => {
    setFormData((prev) => ({ ...prev, items: prev.items.filter((_, idx) => idx !== index) }));
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    if (!newProductForm.name.trim()) {
      toast.error('El nombre del producto es obligatorio');
      return;
    }
    if (!newProductForm.category_id) {
      toast.error('Selecciona una categoría');
      return;
    }
    setNewProductLoading(true);
    try {
      const res = await axios.post(
        `${API}/products`,
        {
          name: newProductForm.name.trim(),
          category_id: newProductForm.category_id,
          price: Number(newProductForm.price) || 0
        },
        { headers: getAuthHeaders() }
      );
      const created = res.data;
      await refreshProducts();
      setFormData((prev) => ({
        ...prev,
        items: [
          ...prev.items,
          {
            product_id: created.id,
            product_name: created.name,
            quantity: 1,
            unit_cost: Number(created.cost) || 0
          }
        ]
      }));
      toast.success('Producto creado y agregado a la orden');
      setNewProductOpen(false);
      setNewProductForm({ name: '', category_id: '', price: '' });
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al crear el producto'));
    }
    setNewProductLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.items.length === 0) {
      toast.error('Agrega al menos un artículo a la orden');
      return;
    }
    if (formData.items.some((i) => Number(i.quantity) <= 0)) {
      toast.error('Todas las cantidades deben ser mayores a 0');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        supplier: formData.supplier || null,
        notes: formData.notes || null,
        items: formData.items.map((i) => ({
          product_id: i.product_id,
          variant_id: i.variant_id || null,
          variant_name: i.variant_name || null,
          product_name: i.product_name || null,
          quantity: Number(i.quantity),
          unit_cost: Number(i.unit_cost) || 0
        }))
      };
      if (editingOrder) {
        await axios.put(`${API}/purchases/${editingOrder.id}`, payload, { headers: getAuthHeaders() });
        toast.success('Orden de compra actualizada');
      } else {
        if (receiveNow) payload.status = 'received';
        await axios.post(`${API}/purchases`, payload, { headers: getAuthHeaders() });
        toast.success(receiveNow ? 'Orden creada y recibida; existencia actualizada' : 'Orden de compra creada');
      }
      await loadData();
      handleCloseDialog();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al guardar la orden'));
    }
    setLoading(false);
  };

  // ======================= Acciones por fila =======================
  const handleReceive = async (order) => {
    try {
      await axios.put(`${API}/purchases/${order.id}/status`, { status: 'received' }, { headers: getAuthHeaders() });
      toast.success('Orden recibida; existencia actualizada');
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al recibir la orden'));
    }
  };

  const handleCancel = async (order) => {
    const wasReceived = order.status === 'received';
    const msg = wasReceived
      ? `¿Cancelar la orden #${order.purchase_number}? Se revertirá la existencia sumada al inventario.`
      : `¿Cancelar la orden #${order.purchase_number}?`;
    if (!window.confirm(msg)) return;
    try {
      await axios.put(`${API}/purchases/${order.id}/status`, { status: 'cancelled' }, { headers: getAuthHeaders() });
      toast.success('Orden cancelada');
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al cancelar la orden'));
    }
  };

  const handleDelete = async (order) => {
    if (!window.confirm(`¿Eliminar la orden #${order.purchase_number}? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/purchases/${order.id}`, { headers: getAuthHeaders() });
      toast.success('Orden eliminada');
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al eliminar la orden'));
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return '—';
    }
  };

  const StatusBadge = ({ status }) => {
    const meta = statusMeta[status] || statusMeta.pending;
    return <Badge variant="outline" className={meta.cls}>{meta.label}</Badge>;
  };

  // ======================= Render =======================
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between gap-3 sm:items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Órdenes de Compra</h2>
          <p className="text-sm text-gray-500">Registra compras a proveedores y alimenta la existencia del inventario al recibirlas</p>
        </div>
        {hasPermission('purchases:create') && (
          <Button
            onClick={handleOpenCreate}
            data-testid="create-purchase-button"
            className="bg-gradient-to-r from-orange-500 to-red-500 rounded-xl"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nueva Orden de Compra
          </Button>
        )}
      </div>

      {/* Tarjetas resumen */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-4 bg-white/80 backdrop-blur-xl border-orange-100">
          <p className="text-xs text-gray-500">Total OC</p>
          <p className="text-2xl font-bold text-gray-800">{totalCount}</p>
        </Card>
        <Card className="p-4 bg-white/80 backdrop-blur-xl border-orange-100">
          <p className="text-xs text-gray-500">Pendientes</p>
          <p className="text-2xl font-bold text-amber-600">{pendingCount}</p>
        </Card>
        <Card className="p-4 bg-white/80 backdrop-blur-xl border-orange-100">
          <p className="text-xs text-gray-500">Recibidas</p>
          <p className="text-2xl font-bold text-green-600">{receivedCount}</p>
        </Card>
        <Card className="p-4 bg-white/80 backdrop-blur-xl border-orange-100">
          <p className="text-xs text-gray-500">Valor comprado</p>
          <p className="text-2xl font-bold text-orange-600">${formatAmount(receivedValue)}</p>
        </Card>
      </div>

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por proveedor o número..."
            className="pl-9 rounded-xl"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {[
            { id: 'all', label: 'Todas' },
            { id: 'pending', label: 'Pendientes' },
            { id: 'received', label: 'Recibidas' },
            { id: 'cancelled', label: 'Canceladas' }
          ].map((f) => (
            <Button
              key={f.id}
              type="button"
              variant={statusFilter === f.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter(f.id)}
              className={`rounded-xl ${statusFilter === f.id ? 'bg-orange-500 hover:bg-orange-600' : 'border-orange-200'}`}
            >
              {f.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Tabla */}
      <Card className="overflow-hidden bg-white/80 backdrop-blur-xl border-orange-100">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gradient-to-r from-orange-500 to-red-500 text-white">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">N°</th>
                <th className="px-4 py-3 text-left font-semibold">Proveedor</th>
                <th className="px-4 py-3 text-left font-semibold">Fecha</th>
                <th className="px-4 py-3 text-left font-semibold">Estado</th>
                <th className="px-4 py-3 text-right font-semibold">Artículos</th>
                <th className="px-4 py-3 text-right font-semibold">Total</th>
                <th className="px-4 py-3 text-center font-semibold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredPurchases.map((order) => (
                <tr key={order.id} className="border-b border-orange-50 hover:bg-orange-50/40">
                  <td className="px-4 py-3 font-semibold text-gray-800">#{order.purchase_number}</td>
                  <td className="px-4 py-3 text-gray-600">{order.supplier || '—'}</td>
                  <td className="px-4 py-3 text-gray-600">{formatDate(order.created_at)}</td>
                  <td className="px-4 py-3"><StatusBadge status={order.status} /></td>
                  <td className="px-4 py-3 text-right text-gray-600">{(order.items || []).length}</td>
                  <td className="px-4 py-3 text-right font-semibold text-orange-600">${formatAmount(order.total || 0)}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-center gap-2 flex-wrap">
                      <Button onClick={() => setViewOrder(order)} variant="outline" size="sm" title="Ver detalle" data-testid={`view-purchase-${order.id}`} className="border-gray-200 hover:bg-gray-50 rounded-xl">
                        <Eye className="w-4 h-4" />
                      </Button>
                      {order.status === 'pending' && hasPermission('purchases:edit') && (
                        <Button onClick={() => handleEdit(order)} variant="outline" size="sm" title="Editar" data-testid={`edit-purchase-${order.id}`} className="border-orange-200 hover:bg-orange-50 rounded-xl">
                          <Edit className="w-4 h-4" />
                        </Button>
                      )}
                      {order.status === 'pending' && hasPermission('purchases:receive') && (
                        <Button onClick={() => handleReceive(order)} variant="outline" size="sm" title="Recibir" data-testid={`receive-purchase-${order.id}`} className="border-green-200 hover:bg-green-50 text-green-600 rounded-xl">
                          <PackageCheck className="w-4 h-4" />
                        </Button>
                      )}
                      {(order.status === 'pending' || order.status === 'received') && hasPermission('purchases:edit') && (
                        <Button onClick={() => handleCancel(order)} variant="outline" size="sm" title="Cancelar" data-testid={`cancel-purchase-${order.id}`} className="border-amber-200 hover:bg-amber-50 text-amber-600 rounded-xl">
                          <Ban className="w-4 h-4" />
                        </Button>
                      )}
                      {order.status !== 'received' && hasPermission('purchases:delete') && (
                        <Button onClick={() => handleDelete(order)} variant="outline" size="sm" title="Eliminar" data-testid={`delete-purchase-${order.id}`} className="border-red-200 hover:bg-red-50 text-red-600 rounded-xl">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredPurchases.length === 0 && (
          <div className="text-center py-12">
            <ShoppingCart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-4">
              {purchases.length === 0 ? 'No hay órdenes de compra registradas' : 'Ninguna orden coincide con el filtro'}
            </p>
            {purchases.length === 0 && hasPermission('purchases:create') && (
              <Button onClick={handleOpenCreate} className="bg-gradient-to-r from-orange-500 to-red-500 rounded-xl">
                <Plus className="w-4 h-4 mr-2" />
                Crear la primera
              </Button>
            )}
          </div>
        )}
      </Card>

      {/* ======================= Diálogo crear/editar ======================= */}
      <Dialog open={isDialogOpen} onOpenChange={(v) => { if (!v) handleCloseDialog(); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingOrder ? `Editar Orden #${editingOrder.purchase_number}` : 'Nueva Orden de Compra'}</DialogTitle>
            <DialogDescription>
              {editingOrder
                ? 'Modifica los artículos de la orden pendiente'
                : 'Agrega productos del catálogo o crea uno nuevo en línea'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Proveedor</Label>
                <Input
                  value={formData.supplier}
                  onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                  placeholder="Nombre del proveedor"
                  className="rounded-xl"
                />
              </div>
              <div>
                <Label>Notas</Label>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Observaciones (opcional)"
                  className="rounded-xl"
                />
              </div>
            </div>

            {/* Selector de producto */}
            <div className="flex flex-col sm:flex-row gap-2">
              <Popover open={productPopoverOpen} onOpenChange={setProductPopoverOpen} >
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={productPopoverOpen}
                    data-testid="purchase-product-combobox"
                    className="flex-1 justify-between rounded-xl border-gray-300 px-3 py-2 text-sm font-normal bg-white hover:bg-white"
                  >
                    <span className="truncate text-left text-gray-500">Agregar producto...</span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Buscar producto..." className="h-9" />
                    <CommandList>
                      <CommandEmpty>Sin productos coincidentes.</CommandEmpty>
                      <CommandGroup>
                        {purchaseOptions.map((opt) => (
                          <CommandItem key={opt.key} value={opt.label} onSelect={() => addProductLine(opt)}>
                            <Check className={`mr-2 h-4 w-4 ${isOptionSelected(opt) ? 'opacity-100' : 'opacity-0'}`} />
                            <span className="truncate">{opt.label}</span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {hasPermission('products:create') && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setNewProductOpen(true)}
                  data-testid="new-product-inline-button"
                  className="rounded-xl border-orange-200 hover:bg-orange-50 text-orange-600 whitespace-nowrap"
                >
                  <Package className="w-4 h-4 mr-2" />
                  Nuevo producto
                </Button>
              )}
            </div>

            {/* Líneas de la orden */}
            <div className="space-y-2">
              {liveItems.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-4">Aún no hay artículos en la orden.</p>
              )}
              {liveItems.map((item, index) => (
                <div key={`${item.product_id}-${index}`} className="flex items-center gap-2 border border-orange-100 rounded-xl p-2 bg-orange-50/30">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">{item.product_name || item.product_id}</p>
                    <p className="text-xs text-gray-500">Subtotal: ${formatAmount(item.subtotal)}</p>
                  </div>
                  <div className="w-24">
                    <Label className="text-xs">Cantidad</Label>
                    <Input
                      type="number"
                      step="1"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value, 10) || 0)}
                      className="rounded-lg h-9"
                    />
                  </div>
                  <div className="w-28">
                    <Label className="text-xs">Costo unit.</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={item.unit_cost}
                      onChange={(e) => updateItem(index, 'unit_cost', parseFloat(e.target.value) || 0)}
                      className="rounded-lg h-9"
                    />
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={() => removeItem(index)} className="rounded-full w-8 h-8 p-0 border-red-200 text-red-600 hover:bg-red-50 mt-4">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>

            {!editingOrder && (
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={receiveNow}
                  onChange={(e) => setReceiveNow(e.target.checked)}
                  className="rounded border-gray-300"
                />
                Recibir ahora (suma la existencia al inventario)
              </label>
            )}

            <div className="flex justify-between items-center border-t border-orange-100 pt-3">
              <div className="text-right">
                <p className="text-sm text-gray-500">Subtotal / Total</p>
                <p className="text-xl font-bold text-orange-600">${formatAmount(liveTotal)}</p>
              </div>
              <Button type="submit" disabled={loading} className="bg-gradient-to-r from-orange-500 to-red-500 rounded-xl">
                {loading ? 'Guardando...' : (editingOrder ? 'Actualizar' : 'Guardar')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ======================= Sub-diálogo nuevo producto ======================= */}
      <Dialog open={newProductOpen} onOpenChange={setNewProductOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nuevo producto</DialogTitle>
            <DialogDescription>Se creará en el catálogo y se agregará a la orden actual.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateProduct} className="space-y-4">
            <div>
              <Label>Nombre</Label>
              <Input
                value={newProductForm.name}
                onChange={(e) => setNewProductForm({ ...newProductForm, name: e.target.value })}
                placeholder="Nombre del producto"
                className="rounded-xl"
                autoFocus
              />
            </div>
            <div>
              <Label>Categoría</Label>
              <select
                value={newProductForm.category_id}
                onChange={(e) => setNewProductForm({ ...newProductForm, category_id: e.target.value })}
                className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm bg-white"
              >
                <option value="">Selecciona una categoría...</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Precio de venta ($)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={newProductForm.price}
                onChange={(e) => setNewProductForm({ ...newProductForm, price: e.target.value })}
                placeholder="0.00"
                className="rounded-xl"
              />
            </div>
            <Button type="submit" disabled={newProductLoading} className="w-full bg-gradient-to-r from-orange-500 to-red-500 rounded-xl">
              {newProductLoading ? 'Creando...' : 'Crear y agregar'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* ======================= Diálogo ver detalle ======================= */}
      <Dialog open={!!viewOrder} onOpenChange={(v) => { if (!v) setViewOrder(null); }}>
        <DialogContent className="max-w-lg">
          {viewOrder && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-orange-500" />
                  Orden #{viewOrder.purchase_number}
                </DialogTitle>
                <DialogDescription>
                  {viewOrder.supplier || 'Sin proveedor'} · {formatDate(viewOrder.created_at)} · <StatusBadge status={viewOrder.status} />
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {(viewOrder.items || []).map((item, idx) => (
                  <div key={`${item.product_id}-${idx}`} className="flex justify-between items-center border-b border-orange-50 py-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-800 truncate">{item.product_name || item.product_id}</p>
                      <p className="text-xs text-gray-500">{item.quantity} × ${formatAmount(item.unit_cost)}</p>
                    </div>
                    <p className="text-sm font-semibold text-orange-600">${formatAmount((Number(item.quantity) || 0) * (Number(item.unit_cost) || 0))}</p>
                  </div>
                ))}
              </div>
              {viewOrder.notes && (
                <div className="text-sm text-gray-600 bg-orange-50 rounded-xl p-3">
                  <span className="font-semibold">Notas:</span> {viewOrder.notes}
                </div>
              )}
              <div className="flex justify-between items-center border-t border-orange-100 pt-3">
                <span className="text-sm text-gray-500">Total</span>
                <span className="text-xl font-bold text-orange-600">${formatAmount(viewOrder.total || 0)}</span>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PurchasesTab;
